<script lang="tsx" setup>
import { CF_Calendar, CF_Checkbox, CF_Select, CF_User } from '@/models/enums-generated'
import type { Column } from '@/stores/column'
import { computed, onBeforeMount, ref, type Ref } from 'vue'
import TextFilter from './TextFilter.vue'
import MultiSelectFilter from './MultiSelectFilter.vue'
import UserFilter from './UserFilter.vue'
import SingleSelectFilter from './SingleSelectFilter.vue'
import { selectItem, TextField, ValueField } from '@/models/select-item'
import toastStore from '@/stores/toast'
import { useI18n } from 'vue-i18n'
import IconButton from '../shared/IconButton.vue'

const { t } = useI18n()
const toaster = toastStore()
import CalendarFilter from '../filter/CalendarFilter.vue'
import type { ColumnGroup } from './shared'
import { viewing } from '@/services/utils'

const props = defineProps<{
	columns: Ref<Column>[]
	hideButton?: boolean
	bookmark?: string
	icon?: string
	groups?: ColumnGroup[]
}>()

const bookmarkName = ref('')
const bookmarks = ref<string[]>([])
const custom = computed(() => props.columns.filter((x) => x.value.customField))

const refreshBookmarks = () => {
	if (!props.bookmark) return

	bookmarks.value = []
	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i)
		if (key && key.startsWith(props.bookmark)) {
			bookmarks.value.push(key.replace(props.bookmark, ''))
		}
	}
}

const saveBookmark = () => {
	const filters: Record<string, any> = {}
	for (const col of props.columns) {
		if (col.value.filter !== undefined) {
			filters[col.value.name] = col.value.filter
		}
	}
	const key = `${props.bookmark}${bookmarkName.value}`
	localStorage.setItem(key, JSON.stringify(filters))
	toaster.showSuccess(t('bookmark.saved'))
	refreshBookmarks()
	bookmarkName.value = ''
}

const loadBookmark = (name: string) => {
	const key = `${props.bookmark}${name}`
	const saved = localStorage.getItem(key)
	if (!saved) {
		toaster.showError(t('bookmark.notFound'))
		return
	}
	const savedFilters = JSON.parse(saved)
	for (const col of props.columns) {
		if (savedFilters[col.value.name] !== undefined) {
			col.value.filter = savedFilters[col.value.name]
		}
	}
}

const deleteBookmark = (name: string) => {
	const key = `${props.bookmark}${name}`
	localStorage.removeItem(key)
	toaster.showSuccess(t('bookmark.deleted'))
	refreshBookmarks()
}

function clearAllFilters() {
	for (const colRef of props.columns) {
		const col = colRef.value
		if (viewing.value > 0 && col.name === 'instance_id') continue

		if (Array.isArray(col.filter)) col.filter = []
		else if (typeof col.filter === 'string') col.filter = ''
		else col.filter = undefined
	}

	const baseUrl = window.location.origin + window.location.pathname
	window.history.replaceState({}, '', baseUrl)
}

onBeforeMount(() => {
	refreshBookmarks()
})
</script>

<template>
	<div class="d-flex align-items-center justify-content-between">
		<div class="pe-2">
			<i class="bi text-secondary fs-2" :class="props.icon ?? 'bi-search'"></i>
			<IconButton id="clear-filters" class="bi bi-x-circle" :title="$t('misc.clearFilters')" @click="clearAllFilters" size="fs-2" />
		</div>

		<div class="d-inline-flex flex-wrap justify-content-start">
			<slot></slot>

			<template v-for="(cf, i) in custom" :key="cf.value.customField?.id">
				<div class="p-2 filter-col" v-if="cf.value.isVisible && cf.value.customField">
					<MultiSelectFilter
						v-if="cf.value.customField.type === CF_Select"
						v-model="custom[i].value"
						:options="cf.value.customField.values"
					></MultiSelectFilter>

					<SingleSelectFilter
						v-else-if="cf.value.customField.type === CF_Checkbox"
						v-model="custom[i].value"
						:label-field="TextField"
						:value-field="ValueField"
						:options="[selectItem('misc.yes', '1'), selectItem('misc.no', '')]"
					></SingleSelectFilter>
					<CalendarFilter
						v-else-if="cf.value.customField.type === CF_Calendar"
						v-model="custom[i].value"
						:label-field="TextField"
						:value-field="ValueField"
					></CalendarFilter>
					<UserFilter v-else-if="cf.value.customField.type === CF_User" v-model="custom[i].value"></UserFilter>

					<TextFilter v-else v-model="custom[i].value"></TextFilter>
				</div>
			</template>
		</div>

		<div class="d-flex align-self-start py-2">
			<div v-if="!props.hideButton" v-one-tooltip="$t('misc.filterOpt')">
				<button
					class="btn btn-outline-secondary dropdown-toggle btn-icon-small me-1"
					type="button"
					id="column-picker"
					data-bs-toggle="dropdown"
					data-bs-auto-close="outside"
					aria-expanded="false"
				>
					<i class="bi bi-menu-button fs-4"></i>
				</button>

				<div class="dropdown-menu" aria-labelledby="column-picker">
					<div class="px-3 column-picker-content">
						<template v-for="(opt, i) in props.columns" :key="opt.value.name">
							<div class="form-check">
								<input
									class="form-check-input"
									type="checkbox"
									:id="'chk-' + i"
									:value="opt.value.name"
									v-model="props.columns[i].value.isVisible"
								/>
								<label class="form-check-label" :for="'chk-' + i">{{ opt.value.label }}</label>
							</div>
						</template>

						<template v-if="props.groups" v-for="(group, g) in props.groups" :key="group.title">
							<h5>{{ group.title }}</h5>
							<template v-for="(opt, i) in group.columns" :key="opt.value.name">
								<div class="form-check">
									<input
										class="form-check-input"
										type="checkbox"
										:id="'chk-' + i"
										:value="opt.value.name"
										v-model="props.groups[g].columns[i].value.isVisible"
									/>
									<label class="form-check-label" :for="'chk-' + i">{{ opt.value.label }}</label>
								</div>
							</template>
						</template>
					</div>
				</div>
			</div>

			<div class="btn-group" v-if="props.bookmark">
				<div class="dropdown me-1" v-one-tooltip="$t('bookmark.save')">
					<button class="btn btn-outline-secondary dropdown-toggle btn-icon-small" type="button" data-bs-toggle="dropdown">
						<i class="bi bi-bookmark-plus fs-4"></i>
					</button>
					<div class="dropdown-menu p-2">
						<input type="text" v-model="bookmarkName" class="form-control mb-2" :placeholder="$t('misc.name')" maxlength="30" />
						<button
							class="btn btn-primary w-100"
							:disabled="!bookmarkName"
							@click="saveBookmark"
							:aria-label="$t('misc.save')"
							role="button"
						>
							<i class="bi bi-floppy"></i>
							{{ $t('misc.save') }}
						</button>
					</div>
				</div>
				<div class="dropdown" v-one-tooltip="$t('bookmark.load')">
					<button class="btn btn-outline-secondary dropdown-toggle btn-icon-small" type="button" data-bs-toggle="dropdown">
						<i class="bi bi-bookmark-star fs-4"></i>
					</button>
					<ul class="dropdown-menu">
						<li v-if="bookmarks.length === 0" class="dropdown-item text-muted">{{ $t('bookmark.empty') }}</li>
						<li v-for="b in bookmarks" :key="b" class="d-flex align-items-center">
							<a class="dropdown-item flex-grow-1" href="#" @click.prevent="loadBookmark(b)">{{ b }}</a>
							<IconButton :id="'delete-bookmark' + bookmarkName" class="bi bi-x" @click="deleteBookmark(b)"></IconButton>
						</li>
					</ul>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="sass">
.filter-col
    width: 256px
.form-check-label
    width: 90%
    text-overflow: ellipsis
    white-space: nowrap
    overflow: clip
</style>

<style lang="sass" scoped>
.dropdown-toggle::after
    display: none !important

.btn-group > .dropdown:first-child .btn
    border-top-right-radius: 0
    border-bottom-right-radius: 0

.btn-group > .dropdown:last-child .btn
    border-top-left-radius: 0
    border-bottom-left-radius: 0

.btn-icon-small
    padding: 0.25rem 0.25rem
    line-height: 1
    height: auto
</style>
